import React, { useEffect, useRef } from 'react'
import type { ControllerRenderProps } from 'react-hook-form'
import { Text, View } from 'react-native'
import { tv } from 'tailwind-variants'

import { useFlowsServicesResponses } from '@flows-ui/services/FlowServicesResponsesContext'
import { useTranslation } from '@flows-ui/utils/i18n'
import { processStepContentElementText } from '@flows-ui/utils/processStepContentElementText'
import { resolveStepContentElementValue } from '@flows-ui/utils/resolveStepContentElementValue'
import { useFormElementField } from '@flows-ui/utils/useFormElementField'
import type { TrackFn } from '@flows/configs/analytics/blurTracking'
import type { AnyCallbackConfig } from '@flows/core/types/dispatchedHookTypes'
import type { CodeInput as CodeInputProps } from '@flows/core/types/stepContentTypes'
import type { AnySimpleOnSubmitHook, FlowValues } from '@flows/core/types/stepTypes'
import UiKitCodeInput from '@ui-kit/components/formComponents/CodeInput'
import { body2 } from '@ui-kit/core/typography'

const codeInputStyles = tv({
  slots: {
    container: 'flex w-full flex-col gap-y-2',
    label: body2({ emphasis: 'medium', align: 'center' }),
  },
})

interface Props extends CodeInputProps<AnyCallbackConfig, AnySimpleOnSubmitHook> {
  flowValues: FlowValues
  track?: TrackFn
}

const toCodeValue = (fieldValue: unknown, configuredValue: string | undefined) => {
  const value = fieldValue ?? configuredValue
  return typeof value === 'string' || typeof value === 'number' ? String(value) : ''
}

const useStoreConfiguredValue = (
  field: ControllerRenderProps,
  configuredValue: string | undefined
) => {
  const isConfiguredValueStored = useRef(false)
  useEffect(() => {
    if (isConfiguredValueStored.current) {
      return
    }
    isConfiguredValueStored.current = true
    if (field.value === undefined && configuredValue !== undefined) {
      field.onChange(configuredValue)
    }
  }, [configuredValue, field])
}

export const CodeInput = (props: Props) => {
  const { t } = useTranslation()
  const servicesResponses = useFlowsServicesResponses()
  const textProcessor = processStepContentElementText(t, {
    flowValues: props.flowValues,
    servicesResponses,
  })
  const styles = codeInputStyles()

  const { field, visibleError, onBlur } = useFormElementField({
    name: props.name,
    onBlur: props.onBlur,
    track: props.track,
  })
  const label = props.label ? textProcessor(props.label) : undefined
  const configuredValue = resolveStepContentElementValue(props.value, textProcessor)
  useStoreConfiguredValue(field, configuredValue)

  return (
    <View className={styles.container()}>
      {label ? <Text className={styles.label()}>{label}</Text> : null}
      <UiKitCodeInput
        testID={props.name}
        fieldRef={field.ref}
        numberOfDigits={props.numberOfDigits}
        align={props.align}
        autoCompleteType={props.autoCompleteType ?? 'off'}
        label={label}
        value={toCodeValue(field.value, configuredValue)}
        disabled={props.disabled}
        autoFocus={props.autoFocus ?? false}
        codeStatus={visibleError ? 'INVALID' : undefined}
        errorMessage={visibleError}
        handleChange={field.onChange}
        onBlur={onBlur}
      />
    </View>
  )
}
